/**
 * ArchiveRoute — /archive
 *
 * Universal cross-project search across all finalized meetings the
 * signed-in user can see. Built on the GET /archive/search endpoint
 * (Postgres FTS via the meetings.tsv generated column).
 *
 * UX (per "ship-with-assumptions" spec):
 *   - Big search input + filter row (Project, Owner, From/To dates,
 *     Attendee text)
 *   - Quick date chips: This month / Last 7d / Last 30d / All
 *   - Result list with title, project, owner avatar, FTS-highlighted
 *     snippet, meeting date, finalized date, cost
 *   - Above-results: total count + active-filter summary line +
 *     sort dropdown
 *   - Click result → /meetings/:id/edit
 *
 * Auth: any signed-in OJB user. Finalized meetings are firm-visible,
 * so this is the right place for cross-project archeology that the
 * per-project search bar can't reach.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { api } from '../lib/apiClient.ts';
import { NO_AUTOFILL } from '../lib/noAutofill.ts';
import { AttendeeAvatar } from '../components/editor/AttendeeAvatar.tsx';

interface ArchiveResult {
  id: string;
  title: string;
  meetingDate: string;
  finalizedAt: string | null;
  ownerUserId: string;
  ownerName: string | null;
  ownerEmail: string | null;
  ownerHeadshotUrl: string | null;
  projectId: string;
  projectName: string | null;
  snippet: string;
  rank: number | null;
  costUsd: number;
}

interface ArchiveResponse {
  results: ArchiveResult[];
  total: number;
  nextOffset: number | null;
  query: string | null;
}

type SortOption = 'relevance' | 'date_desc' | 'date_asc';

export function ArchiveRoute() {
  const [q, setQ] = useState('');
  const [projectId, setProjectId] = useState('');
  const [ownerUserId, setOwnerUserId] = useState('');
  const [attendee, setAttendee] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sort, setSort] = useState<SortOption>('relevance');

  // Debounce the q + attendee inputs slightly so we don't refetch
  // on every keystroke. 250ms feels responsive without thrashing.
  const [debouncedQ, setDebouncedQ] = useState('');
  const [debouncedAttendee, setDebouncedAttendee] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedAttendee(attendee), 250);
    return () => clearTimeout(t);
  }, [attendee]);

  const search = useInfiniteQuery<ArchiveResponse>({
    queryKey: [
      'archive',
      'search',
      {
        q: debouncedQ,
        projectId,
        ownerUserId,
        attendee: debouncedAttendee,
        from,
        to,
        sort,
      },
    ],
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) =>
      api.get<ArchiveResponse>('/archive/search', {
        q: debouncedQ.trim() || undefined,
        projectId: projectId || undefined,
        ownerUserId: ownerUserId || undefined,
        attendee: debouncedAttendee.trim() || undefined,
        from: from || undefined,
        to: to || undefined,
        sort,
        limit: 25,
        offset: pageParam as number,
      }),
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
  });

  const allResults = useMemo(
    () => search.data?.pages.flatMap((p) => p.results) ?? [],
    [search.data],
  );
  const total = search.data?.pages[0]?.total ?? 0;

  // IntersectionObserver for infinite scroll.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          search.hasNextPage &&
          !search.isFetchingNextPage
        ) {
          search.fetchNextPage();
        }
      },
      { rootMargin: '300px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [search]);

  const hasFilters =
    debouncedQ ||
    projectId ||
    ownerUserId ||
    debouncedAttendee ||
    from ||
    to;
  const clearAll = () => {
    setQ('');
    setProjectId('');
    setOwnerUserId('');
    setAttendee('');
    setFrom('');
    setTo('');
  };

  return (
    <div>
      {/* Mockup .archive-intro: 13px charcoal/65. */}
      <p className="mb-3.5 text-[13px] text-charcoal/65">
        Search across every finalized meeting at OJB. Filter by project,
        owner, date, or attendee. Click a result to open it.
      </p>

      {/* Mockup .archive-search card — wraps both the big search
          input AND the filters grid in a single cloud + charcoal/10
          surface. */}
      <div className="mb-3.5 rounded-[14px] border border-charcoal/10 bg-cloud px-4 py-3.5">
        {/* Search input — bone bg + charcoal/20 + rounded-[10px] +
            14px Aeonik with absolutely-positioned icon on the left. */}
        <div className="relative mb-3">
          <Search
            size={14}
            aria-hidden
            className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/45"
          />
          <input
            {...NO_AUTOFILL}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search meeting titles, attendees, decisions, action items…"
            className="w-full rounded-[10px] border border-charcoal/20 bg-bone py-2.5 pl-9 pr-4 text-[14px] text-charcoal placeholder:text-charcoal/45 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
          />
        </div>
        {/* Filters grid — 5 columns on lg+ (Project / Owner /
            Attendee / From / To). Stack-wraps on smaller screens. */}
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_100px_100px] lg:items-end">
          <ProjectPicker value={projectId} onChange={setProjectId} />
          <OwnerPicker value={ownerUserId} onChange={setOwnerUserId} />
          <label className="flex flex-col gap-1 text-[11px]">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-charcoal/60">
              Attendee
            </span>
            <input
              {...NO_AUTOFILL}
              type="text"
              value={attendee}
              onChange={(e) => setAttendee(e.target.value)}
              placeholder="Name or email…"
              className="rounded-[8px] border border-charcoal/20 bg-bone px-2.5 py-1.5 text-[12px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
            />
          </label>
          <label className="flex flex-col gap-1 text-[11px]">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-charcoal/60">
              From
            </span>
            <input
              {...NO_AUTOFILL}
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded-[8px] border border-charcoal/20 bg-bone px-2.5 py-1.5 text-[12px] text-charcoal focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
            />
          </label>
          <label className="flex flex-col gap-1 text-[11px]">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-charcoal/60">
              To
            </span>
            <input
              {...NO_AUTOFILL}
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-[8px] border border-charcoal/20 bg-bone px-2.5 py-1.5 text-[12px] text-charcoal focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
            />
          </label>
        </div>
        {hasFilters && (
          <div className="mt-2.5 flex justify-end">
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1 text-[11px] text-charcoal/55 underline transition-colors hover:text-pine"
            >
              <X size={11} /> Clear all
            </button>
          </div>
        )}
      </div>

      {/* Mockup .results-context: 12px charcoal/65 + bold charcoal
          count + sort select on the right (.results-context__sort). */}
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3 px-1 py-2.5 text-[12px]">
        <div className="text-charcoal/65">
          {search.isLoading ? (
            <span className="italic">Searching…</span>
          ) : total === 0 ? (
            <span className="italic">No matches</span>
          ) : (
            <>
              <b className="font-bold text-charcoal">{total}</b>{' '}
              <span>finalized meeting{total === 1 ? '' : 's'}</span>
              {hasFilters && (
                <span className="ml-1 text-charcoal/45">
                  · {summarizeFilters({ q: debouncedQ, projectId, ownerUserId, attendee: debouncedAttendee, from, to })}
                </span>
              )}
            </>
          )}
        </div>
        <label className="flex items-center gap-2 text-[11px]">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-charcoal/60">
            Sort
          </span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortOption)}
            className="rounded-[8px] border border-charcoal/20 bg-bone px-2.5 py-1 text-[12px] text-charcoal focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
          >
            <option value="relevance">Relevance</option>
            <option value="date_desc">Newest first</option>
            <option value="date_asc">Oldest first</option>
          </select>
        </label>
      </div>

      {/* Results */}
      {search.error && (
        <div className="rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 p-4 text-[13px] text-terra-cotta">
          {String((search.error as Error).message)}
        </div>
      )}
      {!search.isLoading && allResults.length === 0 && !search.error && (
        <div className="rounded-[14px] border border-dashed border-charcoal/20 bg-cloud/40 px-4 py-12 text-center text-[13px] italic text-charcoal/55">
          {hasFilters
            ? 'No finalized meetings match your filters. Try widening the date range or clearing a chip.'
            : 'Start typing to search every finalized meeting at OJB.'}
        </div>
      )}
      {allResults.length > 0 && (
        <ul className="space-y-2">
          {allResults.map((r) => (
            <ResultRow key={r.id} result={r} />
          ))}
        </ul>
      )}
      {search.hasNextPage && (
        <div
          ref={sentinelRef}
          className="py-6 text-center text-[11px] italic text-charcoal/45"
        >
          {search.isFetchingNextPage ? 'Loading more…' : ' '}
        </div>
      )}
    </div>
  );
}

/* ─── Result row ────────────────────────────────────────────────── */

function ResultRow({ result }: { result: ArchiveResult }) {
  // Mockup .result-row: bone bg + charcoal/8 border + rounded-[14px]
  // + 16/20 padding. Hover: pine/30 border + cloud/85 wash +
  // -translate-y-px lift. Title 15px font-medium, date 11px tabular,
  // snippet 13px line-1.55 with pine-soft <b> highlights.
  return (
    <li>
      <Link
        to={`/meetings/${result.id}/edit`}
        className="block rounded-[14px] border border-charcoal/8 bg-bone px-5 py-4 no-underline transition-all hover:-translate-y-px hover:border-pine/30 hover:bg-cloud/85 hover:shadow-[0_4px_12px_rgba(43,42,47,0.06)]"
      >
        <div className="mb-1 flex items-baseline justify-between gap-3">
          <h3 className="truncate text-[15px] font-medium text-charcoal">
            {result.title || (
              <span className="italic text-charcoal/45">Untitled</span>
            )}
          </h3>
          <span className="shrink-0 text-[11px] tabular-nums text-charcoal/55">
            {result.meetingDate}
          </span>
        </div>

        {/* Snippet — server pre-escapes the entire ts_headline
            output (security audit H1) and then introduces safe
            <mark> tags around matched terms via per-request random
            sentinels. So everything in result.snippet is either
            HTML-entity-escaped text or our own <mark> markup; no
            user-supplied HTML can survive. dangerouslySetInnerHTML
            is therefore safe here. .snippet-highlight (globals.css)
            styles the <mark> tags as pine-soft pills. */}
        {result.snippet && (
          <p
            className="snippet-highlight mb-2 line-clamp-2 text-[13px] leading-[1.55] text-charcoal/78"
            dangerouslySetInnerHTML={{ __html: result.snippet }}
          />
        )}

        {/* Meta row — mockup .result-row__meta: 11px charcoal/60 +
            3px round dot separators (matches the dashboard meeting
            pill meta family). */}
        <div className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[11px] text-charcoal/60">
          {result.projectName && (
            <span className="inline-flex items-baseline gap-1">
              <span>project</span>
              <b className="font-bold text-charcoal">{result.projectName}</b>
            </span>
          )}
          {result.ownerName && (
            <>
              <span aria-hidden className="h-[3px] w-[3px] rounded-full bg-current opacity-50" />
              <span className="inline-flex items-center gap-1.5">
                <AttendeeAvatar
                  name={result.ownerName}
                  headshotUrl={result.ownerHeadshotUrl}
                  size={18}
                />
                <span>{result.ownerName}</span>
              </span>
            </>
          )}
          {result.finalizedAt && (
            <>
              <span aria-hidden className="h-[3px] w-[3px] rounded-full bg-current opacity-50" />
              <span>
                finalized {new Date(result.finalizedAt).toLocaleDateString()}
              </span>
            </>
          )}
          {result.costUsd > 0 && (
            <>
              <span aria-hidden className="h-[3px] w-[3px] rounded-full bg-current opacity-50" />
              <span className="tabular-nums">${result.costUsd.toFixed(2)}</span>
            </>
          )}
        </div>
      </Link>
    </li>
  );
}

/* ─── Filter helpers ───────────────────────────────────────────── */

function ProjectPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (id: string) => void;
}) {
  const projectsQ = useQuery({
    queryKey: ['projects', 'all'],
    queryFn: () =>
      api.get<{ projects: Array<{ id: string; name: string }> }>('/projects', {
        limit: 500,
      }),
    staleTime: 60_000,
  });
  return (
    <label className="flex flex-col gap-1 text-[11px]">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-charcoal/60">
        Project
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-[8px] border border-charcoal/20 bg-bone px-2.5 py-1.5 text-[12px] text-charcoal focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
      >
        <option value="">Any project</option>
        {(projectsQ.data?.projects ?? []).map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function OwnerPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (id: string) => void;
}) {
  // /admin/users is admin-only. For non-admin users we just hide the
  // owner filter — the search bar + project filter are usually
  // enough scoping. Future: expose a non-admin /users endpoint that
  // returns just id+displayName so any authed user can filter by
  // owner without leaking spend / admin flags.
  const usersQ = useQuery({
    queryKey: ['admin', 'users', 'archive-picker'],
    queryFn: () =>
      api.get<{
        users: Array<{ id: string; displayName: string; email: string }>;
      }>('/admin/users'),
    staleTime: 60_000,
    retry: false,
  });
  if (usersQ.isError) {
    // 403 from /admin/users on non-admin caller — just don't render.
    return null;
  }
  return (
    <label className="flex flex-col gap-1 text-[11px]">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-charcoal/60">
        Owner
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-[8px] border border-charcoal/20 bg-bone px-2.5 py-1.5 text-[12px] text-charcoal focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
      >
        <option value="">Any owner</option>
        {(usersQ.data?.users ?? []).map((u) => (
          <option key={u.id} value={u.id}>
            {u.displayName} - {u.email}
          </option>
        ))}
      </select>
    </label>
  );
}

function summarizeFilters(f: {
  q: string;
  projectId: string;
  ownerUserId: string;
  attendee: string;
  from: string;
  to: string;
}): string {
  const bits: string[] = [];
  if (f.q) bits.push(`"${f.q}"`);
  if (f.from && f.to) bits.push(`${f.from} → ${f.to}`);
  else if (f.from) bits.push(`from ${f.from}`);
  else if (f.to) bits.push(`up to ${f.to}`);
  if (f.attendee) bits.push(`attendee: ${f.attendee}`);
  if (f.projectId) bits.push('project filter');
  if (f.ownerUserId) bits.push('owner filter');
  return bits.join(' · ');
}
